import React, { Component } from 'react';
import { createParty, restParty, equipItem, unequipSlot, dropItem, giveItem, transferItem, useItem } from './party';
import { listParties, saveParty, deleteParty, partyFromSave } from './saves';
import { generateItem, generatePotion, itemName } from './items';
import { levelUp, trainingBlocker, trainingCost } from './leveling';
import { SPELLS as ALL_SPELLS, teachableTo } from './combat/spells';
import { spawnMonsters, stepMonsters, isWithinReach, headingToward, monsterAt, findTargetInSight, findFloorSpot } from './combat/monsters';
import { combatFoeLayout, EFFECT_DURATIONS } from './viewMath';
import { createExplored, revealAround } from './fogOfWar';
import { seededRandom, levelSeed, randomSeed, normaliseSeed } from './rng';
import { buildDungeon, levelOnArrival, TRAINER_EVERY, TILE, TILE_FACILITIES } from './dungeon';
import { levelFromMap, ENTRANCES, DEFAULT_ENTRANCE } from './maps';
import { healCost, healed, templeBlocker, buyPrice, sellPrice, marketBlocker, roomBlocker, ROOM_PRICE, RUMOURS } from './services';
import { playSound, playSounds } from './audio';
import { startCombat, performPartyAction, joinCombat, rangedVolley, castOutsideCombat, currentActorId, healBlocker } from './combat/combatEngine';
import { SPELLS, spellCost, castableOutsideCombat } from './combat/spells';
import { isAble, rollDie, statBonus, applyCharacterDamage } from './combat/rules';


const StateContext = React.createContext('test');

// Explored grid for a brand new level: just what's visible from the start
function freshExplored(tiles, coords) {
    return revealAround(createExplored(tiles), tiles, coords);
}

export const DEFAULT_RUN_LEVELS = 10;
// What each of the town's trades is called in the log and on its sign
export const FACILITY_NAMES = {
    training: 'training hall',
    magic: 'magic school',
    market: 'market',
    temple: 'temple',
    tavern: 'tavern',
};
// How often a slain monster is carrying something to drink
const POTION_DROP_CHANCE = 0.4;
export { TRAINER_EVERY };
// Gold found in a chest that doesn't hold the key
const CHEST_GOLD = [15, 50];
// A purse for fresh recruits, so the town's trades are worth visiting before
// the party has looted anything
const STARTING_GOLD = 200;

function doorStatesFrom(doors) {
    const states = {};
    (doors || []).forEach((door) => {
        states[`${door.x},${door.y}`] = { isOpen: door.isOpen, orientation: door.orientation };
    });
    return states;
}

// One dungeon level of a run. The same seed and level number always give the
// same layout, chests and monsters, because the generators are run with
// Math.random seeded (see rng.js).
// The live fields for whichever level the party is standing on
function levelState(record, at, heading = 0) {
    return {
        theme: record.theme,
        // True on the town level, where the rules of the dungeon don't apply
        town: !!record.town,
        // What each square is floored with, on a map that was painted
        floors: record.floors || null,
        // Which squares carry a shop front beside their door
        surrounds: record.surrounds || null,
        // A painted town names its own way down, and where it stands
        entrance: record.gate || null,
        entrancePanel: record.entranceStyle
            ? `./assets/ui/${(ENTRANCES[record.entranceStyle] || ENTRANCES[DEFAULT_ENTRANCE]).panel}.jpg`
            : null,
        player_coords: at,
        player_heading: heading,
        player_map: record.tiles,
        doorStates: record.doorStates,
        keyChestLocation: record.keyChestLocation,
        monsters: record.monsters,
        explored: levelOnArrival(record, at),
        hasKey: record.hasKey,
        retreatCoords: null,
        combat: null,
        spellcasting: null,
        characterSheet: null,
        selectedItem: null,
        // Which of the town's trades is open, if any...
        facility: null,
        // ...and which character is doing the talking there
        facilityCharacter: 0,
        // What the merchant has left, on the level that has one
        shopStock: record.shop || null,
        // True while the full map is open
        mapOpen: false,
        // True while the gem's game menu is open
        gameMenuOpen: false,
        effects: [],
        busy: false,
        gameOver: false
    };
}

// Everything about the level the party is leaving, to be picked up again later
function storeLevel(dungeon, level, state) {
    return dungeon.map((record) => (record.level !== level ? record : {
        ...record,
        tiles: state.player_map,
        doorStates: state.doorStates,
        monsters: state.monsters,
        explored: state.explored,
        hasKey: state.hasKey,
        shop: state.shopStock,
    }));
}

function startingState(dungeon) {
    const first = dungeon[0];
    return { dungeon, ...levelState(first, first.start) };
}

function emptyStats() {
    return { monsters: 0, kills: {}, gold: 0, xp: 0, chests: 0, items: 0, itemValue: 0, levels: 0 };
}

const totalXp = (party) => party.reduce((sum, c) => sum + c.xp, 0);

// Monsters that engage the party step onto its square to fight
function moveOntoParty(monsters, engaged, [x, y]) {
    const ids = new Set(engaged.map((m) => m.id));
    return monsters.map((m) => (ids.has(m.id) ? { ...m, x, y } : m));
}

// Seconds between successive effects (e.g. shots in a volley)
const EFFECT_STAGGER = 0.18;
let nextEffectId = 1;

// Levels are generated per run from the run's seed; see buildLevel above.

export class StateProvider extends Component {
    state = {
        eventLog:[{type:'voice',message:'Welcome Adventurer....'}],

        // 'menu' before a run starts, 'playing' during it, 'complete' on the summary
        screen: 'menu',
        // { seed, levels, level, startedAt, stats } while a run is under way
        run: null,
        // Summary of the finished run, shown on the completion screen
        runResult: null,
        // The saved party in play, if this run started from one
        savedParty: null,

        // The town sits behind the launch menu, so the 3D view has something to
        // draw: two levels, since a single-level run is all dungeon
        ...startingState(buildDungeon('FRPG-MENU', 2)),

        party: createParty(),
        gold: 0
    };

    // ---------- Runs ----------

    // What the slain were carrying: potions, often enough to be worth searching
    addDrops(prev, update, kills) {
        if (!kills || !kills.length) return update;
        const level = prev.run ? prev.run.level : 1;
        let party = update.party || prev.party;
        const messages = [];
        let found = 0;
        let worth = 0;

        kills.forEach((kill) => {
            if (Math.random() > POTION_DROP_CHANCE) return;
            const potion = generatePotion(level);
            const handed = giveItem(party, potion);
            if (!handed.taker) {
                messages.push({ type: 'dev', message: `A ${potion.kind} is left behind: every pack is full.` });
                return;
            }
            party = handed.party;
            found += 1;
            worth += potion.value;
            messages.push({
                type: 'voice',
                message: `${handed.taker.name} takes a ${potion.kind} from the ${kill.name}.`
            });
        });

        if (!found && !messages.length) return update;
        const withDrops = {
            ...update,
            party,
            eventLog: [...(update.eventLog || prev.eventLog), ...messages],
        };
        const run = update.run || prev.run;
        if (run && found) {
            withDrops.run = {
                ...run,
                stats: { ...run.stats, items: run.stats.items + found, itemValue: run.stats.itemValue + worth },
            };
        }
        return withDrops;
    }

    // Fold a state update's kills, gold and experience into the run's tally
    withRunStats(prev, update) {
        if (!prev.run || prev.screen !== 'playing') return update;
        const stats = { ...prev.run.stats, kills: { ...prev.run.stats.kills } };
        if (update.monsters) {
            prev.monsters
                .filter((m) => !update.monsters.some((n) => n.id === m.id))
                .forEach((m) => {
                    stats.monsters += 1;
                    stats.kills[m.name] = (stats.kills[m.name] || 0) + 1;
                });
        }
        if (update.gold !== undefined) stats.gold += Math.max(0, update.gold - prev.gold);
        if (update.party) stats.xp += Math.max(0, totalXp(update.party) - totalXp(prev.party));
        return { ...update, run: { ...prev.run, stats } };
    }

    // The state update that finishes a run and shows the summary
    endRun(state, outcome) {
        const run = state.run;
        return {
            screen: 'complete',
            run: null,
            runResult: {
                outcome,
                seed: run.seed,
                levels: run.levels,
                level: run.level,
                elapsed: Date.now() - run.startedAt,
                stats: run.stats,
                gold: state.gold,
                party: state.party
            }
        };
    }

    // `save` is a saved party to take back down, or null for fresh recruits
    // `map` is a town painted in the editor to play in, instead of a generated one
    startRun = (seed, levels = DEFAULT_RUN_LEVELS, save = null, map = null) => {
        const runSeed = normaliseSeed(seed);
        const dungeon = buildDungeon(runSeed, levels, map ? levelFromMap(map) : null);
        const party = save ? partyFromSave(save) : createParty();
        const town = dungeon[0].town ? dungeon[0].name : null;
        const opening = save
            ? `${save.name} return to ${town || 'the dungeon'}. Seed ${runSeed}, ${levels} levels.`
            : `The party gathers in ${town || 'the dungeon'}. Seed ${runSeed}, ${levels} levels.`;

        this.setState({
            ...startingState(dungeon),
            screen: 'playing',
            run: { seed: runSeed, levels, level: 1, startedAt: Date.now(), stats: emptyStats(), town },
            runResult: null,
            party,
            gold: save ? save.gold : STARTING_GOLD,
            // Which saved party is in play, so the summary can offer to update it
            savedParty: save ? { name: save.name, runs: save.runs || 0, deepest: save.deepest || 0 } : null,
            eventLog: [
                { type: 'voice', message: opening },
                { type: 'voice', message: town
                    ? 'Spend your gold at the temple, market and magic school, then take the gate down.'
                    : 'Find the key, then take the stairs.' }
            ]
        });
    }

    // Keep these adventurers for another day
    saveParty = (name) => {
        const prev = this.state;
        const source = prev.runResult || prev;
        const known = prev.savedParty || {};
        const deepest = Math.max(known.deepest || 0, (prev.runResult && prev.runResult.level) || (prev.run && prev.run.level) || 0);
        const save = saveParty(name, source.party, source.gold, { runs: known.runs || 0, deepest });
        if (!save) {
            this.say('The party could not be saved.', 'dev');
            return null;
        }
        playSound('key');
        this.setState({ savedParty: { name: save.name, runs: save.runs, deepest: save.deepest } });
        this.say(`${save.name} are recorded in the annals.`);
        return save;
    }

    // Take the staircase the party is standing on: up, down, or out
    useStairs = () => {
        const prev = this.state;
        if (!prev.run) return;
        const [x, y] = prev.player_coords;
        const record = prev.dungeon[prev.run.level - 1];
        const link = record && record.stairs[`${x},${y}`];
        if (!link) return;

        if (link.exit) {
            playSounds(['stairs', 'victory'], 0.35);
            this.setState(this.endRun(prev, 'victory'));
            return;
        }

        const dungeon = storeLevel(prev.dungeon, prev.run.level, prev);
        const target = dungeon[link.toLevel - 1];
        const climbing = link.toLevel > prev.run.level;
        playSound('stairs');
        this.setState({
            dungeon,
            run: { ...prev.run, level: link.toLevel },
            ...levelState(target, link.toPos, prev.player_heading),
            eventLog: [...prev.eventLog, {
                type: 'voice',
                message: link.vault
                    ? `The stairs open into a sealed chamber on level ${link.toLevel}.`
                    : `You ${climbing ? 'climb' : 'descend'} to level ${link.toLevel} of ${prev.run.levels}...`
            }]
        });
    }

    // ---------- The map editor ----------

    openEditor = () => {
        playSound('click');
        this.setState({ screen: 'editor', mapOpen: false, gameMenuOpen: false, characterSheet: null });
    }

    closeEditor = () => {
        playSound('click');
        this.setState({ screen: this.state.run ? 'playing' : 'menu' });
    }

    returnToMenu = () => {
        this.setState({
            screen: 'menu',
            run: null,
            runResult: null,
            ...startingState(buildDungeon('FRPG-MENU', 2)),
            party: createParty(),
            gold: 0,
            eventLog: [{ type: 'voice', message: 'Welcome Adventurer....' }]
        });
    }

    // Animate missiles and spells, then apply the outcome once the last one ends.
    // Input is locked meanwhile, so `update` (computed beforehand) stays valid.
    playEffects(effects, update) {
        if (!effects.length) {
            this.setState(update);
            return;
        }
        const now = performance.now() / 1000;
        // Aim at where each target is drawn: its spot in the combat lineup,
        // or the centre of its square when not fighting
        const { monsters, combat, player_coords, player_heading } = this.state;
        const layout = combatFoeLayout(monsters, combat, player_coords, player_heading);
        const aimAt = (target) => target && {
            ...target,
            position: layout[target.id] ? layout[target.id].position : [target.x, 0, target.y],
            scale: layout[target.id] ? layout[target.id].scale : 1
        };
        const scheduled = effects.map((effect, index) => ({
            ...effect,
            target: aimAt(effect.target),
            id: 'fx' + (nextEffectId++),
            from: this.state.player_coords,
            heading: this.state.player_heading,
            start: now + index * EFFECT_STAGGER,
            duration: EFFECT_DURATIONS[effect.type === 'missile' ? 'missile' : effect.spellId] || 0.6
        }));
        scheduled.forEach((effect, index) => {
            playSound(effect.type === 'missile' ? 'bowShot' : 'spellCast', index * EFFECT_STAGGER);
        });
        const end = Math.max(...scheduled.map((e) => e.start + e.duration));
        this.setState({ effects: scheduled, busy: true });
        setTimeout(() => this.setState({ ...update, effects: [], busy: false }), (end - now) * 1000);
    }

    say(message, type = 'voice') {
        this.setState((prev) => ({ eventLog: [...prev.eventLog, { type, message }] }));
    }

    // Cast the spell chosen through the casting menus
    castChosenSpell(casterId, spellId, allyId) {
        const prev = this.state;
        // Close the menus straight away rather than leaving them up during the animation
        this.setState({ spellcasting: null });
        if (prev.combat) {
            const { update, effects } = this.resolveCombatAction(prev, 'cast', { spellId, allyId });
            this.playEffects(effects, update);
            return;
        }
        const result = castOutsideCombat(prev, casterId, spellId, { allyId });
        const afterCast = { ...prev, party: result.party, eventLog: [...prev.eventLog, ...result.messages] };
        if (!result.cast) {
            this.setState({ eventLog: afterCast.eventLog });
            return;
        }
        // Casting takes a game turn, so monsters advance afterwards
        this.playEffects(result.effects, {
            party: afterCast.party,
            eventLog: afterCast.eventLog,
            ...this.advanceMonsters(afterCast, prev.player_coords)
        });
    }

    // ---------- Character sheet ----------

    openCharacterSheet = (index = 0) => {
        if (this.state.combat || this.state.gameOver || this.state.screen !== 'playing') return;
        this.setState({ characterSheet: index });
    }

    closeCharacterSheet = () => {
        this.setState({ characterSheet: null, selectedItem: null });
    }

    // Drink a potion from a character's pack
    useItem = (index, itemId) => {
        this.setState((prev) => {
            const character = prev.party[index];
            if (!character) return null;
            const result = useItem(character, itemId);
            if (!result.item) return null;
            if (result.wasted) {
                return { eventLog: [...prev.eventLog, { type: 'dev', message: `${character.name} has no use for that.` }] };
            }
            if (!result.restored) {
                return { eventLog: [...prev.eventLog, { type: 'dev', message: `${character.name} is already at full strength.` }] };
            }
            playSound('heal');
            const kind = result.item.restores === 'hp' ? 'hit points' : 'spell points';
            return {
                party: prev.party.map((c, i) => (i === index ? result.character : c)),
                selectedItem: null,
                eventLog: [...prev.eventLog, {
                    type: 'voice',
                    message: `${character.name} drinks the ${result.item.kind} and recovers ${result.restored} ${kind}.`
                }]
            };
        });
    }

    // Pick an item up (or put it down again) ready to hand to another character
    selectItem = (index, itemId) => {
        this.setState((prev) => {
            const chosen = prev.selectedItem;
            if (chosen && chosen.index === index && chosen.itemId === itemId) {
                return { selectedItem: null };
            }
            playSound('click');
            return { selectedItem: { index, itemId } };
        });
    }

    // Hand the selected item to the character in the given party slot
    transferItem = (toIndex) => {
        this.setState((prev) => {
            const chosen = prev.selectedItem;
            if (!chosen) return null;
            if (chosen.index === toIndex) return { selectedItem: null };
            const result = transferItem(prev.party, chosen.index, toIndex, chosen.itemId);
            if (!result.item) return { selectedItem: null };
            if (result.full) {
                return {
                    selectedItem: null,
                    eventLog: [...prev.eventLog, { type: 'dev', message: `${prev.party[toIndex].name}'s pack is full.` }]
                };
            }
            playSound('click');
            return {
                party: result.party,
                selectedItem: null,
                eventLog: [...prev.eventLog, {
                    type: 'voice',
                    message: `${prev.party[chosen.index].name} hands the ${itemName(result.item)} to ${prev.party[toIndex].name}.`
                }]
            };
        });
    }

    openChest = ([x, y]) => {
        this.setState((prev) => {
            if (prev.player_map[y][x] !== 8) return null;
            const map = [...prev.player_map];
            map[y] = [...map[y]];
            map[y][x] = 0;
            const keyChest = prev.keyChestLocation;
            const holdsKey = keyChest && keyChest.x === x && keyChest.y === y && !prev.hasKey;

            playSounds(holdsKey ? ['chest', 'key'] : ['chest']);
            let update;
            if (holdsKey) {
                update = {
                    player_map: map,
                    hasKey: true,
                    eventLog: [...prev.eventLog, { type: 'voice', message: 'You found a key in the chest!' }]
                };
            } else {
                const seed = prev.run ? levelSeed(prev.run.seed, prev.run.level) : 'chest';
                const rng = seededRandom(`${seed}:chest:${x},${y}`);
                const gold = CHEST_GOLD[0] + Math.floor(rng() * (CHEST_GOLD[1] - CHEST_GOLD[0] + 1));
                const messages = [{ type: 'voice', message: `You found ${gold} gold in the chest!` }];
                let party = prev.party;
                let loot = null;

                // Roughly half the chests also hold an item
                if (rng() < 0.5) {
                    loot = generateItem(prev.run ? prev.run.level : 1, rng);
                    const handed = giveItem(party, loot);
                    if (handed.taker) {
                        party = handed.party;
                        messages.push({ type: 'voice', message: `${handed.taker.name} takes a ${itemName(loot)}.` });
                        playSound('key', 0.25);
                    } else {
                        loot = null;
                        messages.push({ type: 'dev', message: 'Nobody has room for anything more.' });
                    }
                }

                update = {
                    player_map: map,
                    party,
                    gold: prev.gold + gold,
                    eventLog: [...prev.eventLog, ...messages]
                };
                if (loot) update.loot = loot;
            }

            // Every chest counts, the key one included
            const loot = update.loot;
            delete update.loot;
            const withStats = this.withRunStats(prev, update);
            if (withStats.run) {
                const stats = { ...withStats.run.stats, chests: withStats.run.stats.chests + 1 };
                if (loot) {
                    stats.items += 1;
                    stats.itemValue += loot.value;
                }
                withStats.run = { ...withStats.run, stats };
            }
            return withStats;
        });
    }
    toggleDoor = (x, y) => {
        const key = `${x},${y}`;
        const doorStates = {...this.state.doorStates};
        const map = [...this.state.player_map];
        const currentTile = map[y][x];
        
        // Check if it's a door tile (3-6)
        if (currentTile >= 3 && currentTile <= 6) {
            const doorState = doorStates[key];
            if (doorState && doorState.locked) {
                playSound('bump');
                this.say('The door is locked. Bash it down (B).');
                return false;
            }
            if (doorState) {
                doorState.isOpen = !doorState.isOpen;
                
                // Update the tile based on orientation and open state
                if (doorState.orientation === 'horizontal') {
                    map[y][x] = doorState.isOpen ? 4 : 3;
                } else {
                    map[y][x] = doorState.isOpen ? 6 : 5;
                }
                
                playSound(doorState.isOpen ? 'doorOpen' : 'doorClose');
                this.setState({
                    doorStates,
                    player_map: map,
                    // An opened door lets the party see through it
                    explored: revealAround(this.state.explored, map, this.state.player_coords),
                    eventLog: [...this.state.eventLog, {
                        type: 'voice',
                        message: doorState.isOpen ? 'You opened the door.' : 'You closed the door.'
                    }]
                });
                return true;
            }
        }
        return false;
    }


    // One action for whatever the party is standing on, or the door beside
    // them: stairs, chests, the trainer's desk. Bound to F and the USE button.
    interact = () => {
        const prev = this.state;
        if (prev.combat || prev.busy || prev.gameOver || prev.facility ||
            prev.spellcasting || prev.characterSheet !== null || prev.screen !== 'playing') return;

        const [x, y] = prev.player_coords;
        const tile = prev.player_map[y][x];

        if (tile === TILE.chest) {
            this.openChest([x, y]);
            return;
        }
        if (TILE_FACILITIES[tile]) {
            this.openFacility(TILE_FACILITIES[tile]);
            return;
        }
        if (tile === TILE.stairsUp || tile === TILE.stairsDown) {
            const record = prev.run && prev.dungeon[prev.run.level - 1];
            const link = record && record.stairs[`${x},${y}`];
            if (!link) return;
            if (link.locked && !prev.hasKey) {
                playSound('bump');
                this.say('The stairs are locked. You need to find a key!');
                return;
            }
            this.say(link.exit ? 'You unlock the final stairs and climb towards daylight...'
                               : 'You take the stairs...');
            setTimeout(this.useStairs, 400);
            return;
        }

        // Otherwise, a door within reach
        const door = this.adjacentDoor(x, y);
        if (door) {
            this.toggleDoor(door.x, door.y);
            return;
        }
        playSound('bump');
        this.say('There is nothing here to use.', 'dev');
    }

    // What the party is standing next to or on, as a nudge in the log
    describeHere = () => {
        const prev = this.state;
        const [x, y] = prev.player_coords;
        const tile = prev.player_map[y][x];
        const what = tile === TILE.chest ? 'open the chest'
            : TILE_FACILITIES[tile] ? `go into the ${FACILITY_NAMES[TILE_FACILITIES[tile]]}`
            : tile === TILE.stairsUp || tile === TILE.stairsDown ? 'take the stairs'
            : null;
        if (what) {
            this.say(`Press F to ${what}`, 'dev');
            return;
        }
        const door = this.adjacentDoor(x, y);
        if (door) this.say(`Press F to ${door.isOpen ? 'close' : 'open'} the door`, 'dev');
    }

    adjacentDoor(x, y) {
        const map = this.state.player_map;
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
            const row = map[y + dy];
            const tile = row && row[x + dx];
            if (tile >= 3 && tile <= 6) {
                return { x: x + dx, y: y + dy, isOpen: tile === 4 || tile === 6 };
            }
        }
        return null;
    }

    // Shoulder a door: the first two able characters throw themselves at it.
    // A sturdy door can knock them about in return.
    bashDoor = () => {
        const prev = this.state;
        if (prev.combat || prev.busy || prev.screen !== 'playing' || prev.facility ||
            prev.characterSheet !== null || prev.mapOpen) return;

        const [px, py] = prev.player_coords;
        const door = this.adjacentDoor(px, py);
        const state = door && prev.doorStates[`${door.x},${door.y}`];
        if (!door || !state) {
            playSound('bump');
            this.say('There is no door here to bash.', 'dev');
            return;
        }
        if (door.isOpen) {
            this.say('The door is already open.', 'dev');
            return;
        }

        const bashers = prev.party.filter(isAble).slice(0, 2);
        if (!bashers.length) {
            this.say('Nobody is in any state to bash a door.', 'dev');
            return;
        }

        const messages = [];
        const doorStates = { ...prev.doorStates };
        const hurt = {};
        let hp = state.hp;

        bashers.forEach((character) => {
            if (hp <= 0) return;
            const damage = Math.max(1, rollDie(6) + statBonus(character.stats.might));
            hp -= damage;
            messages.push({ type: 'combat', message: `${character.name} slams into the door for ${damage} damage.` });
            // A stout door gives as good as it gets
            if (hp > 0 && Math.random() < state.endurance * 0.05) {
                const bruise = rollDie(3);
                hurt[character.id] = (hurt[character.id] || 0) + bruise;
                messages.push({ type: 'hurt', message: `The door holds fast; ${character.name} takes ${bruise} damage.` });
            }
        });

        const party = prev.party.map((c) => {
            if (!hurt[c.id]) return c;
            const wounded = { ...c };
            applyCharacterDamage(wounded, hurt[c.id]);
            return wounded;
        });

        if (hp > 0) {
            playSound('bash');
            doorStates[`${door.x},${door.y}`] = { ...state, hp };
            const share = hp / state.maxHp;
            messages.push({
                type: 'dev',
                message: share > 0.6 ? 'The door barely shifts.'
                    : share > 0.3 ? 'The timbers are starting to give.'
                    : 'The door is close to breaking.'
            });
            this.setState({ party, doorStates, eventLog: [...prev.eventLog, ...messages] });
            return;
        }

        // The door gives way, leaving a jagged hole
        playSounds(['bash', 'doorBreak'], 0.12);
        const map = prev.player_map.map((row) => [...row]);
        map[door.y][door.x] = TILE.brokenDoor;
        delete doorStates[`${door.x},${door.y}`];
        messages.push({ type: 'victory', message: 'The door splinters and gives way!' });
        this.setState({
            party,
            doorStates,
            player_map: map,
            explored: revealAround(prev.explored, map, prev.player_coords),
            eventLog: [...prev.eventLog, ...messages]
        });
    }

    // The gem under the view: pause, save the party, or start again
    toggleGameMenu = () => {
        const prev = this.state;
        if (prev.busy || prev.screen !== 'playing') return;
        this.setState({ gameMenuOpen: !prev.gameMenuOpen, characterSheet: null, mapOpen: false, selectedItem: null });
    }

    closeGameMenu = () => {
        this.setState({ gameMenuOpen: false });
    }

    // The full map of the level, as far as the party has explored it
    toggleMap = () => {
        const prev = this.state;
        if (prev.combat || prev.busy || prev.screen !== 'playing') return;
        playSound('click');
        this.setState({ mapOpen: !prev.mapOpen, characterSheet: null, selectedItem: null });
    }

    closeMap = () => {
        this.setState({ mapOpen: false });
    }

    // ---------- The town's trades ----------

    openFacility = (kind) => {
        if (this.state.combat || this.state.screen !== 'playing') return;
        playSound('key');
        this.setState({ facility: kind, facilityCharacter: 0, characterSheet: null, selectedItem: null });
    }

    // Who steps up to the counter: their own gear is what the merchant sees,
    // their own spells what the school will teach, and their own way with
    // people what the price ends up being
    chooseFacilityCharacter = (index) => {
        const { facility, party } = this.state;
        if (!facility || !party[index]) return;
        playSound('click');
        this.setState({ facilityCharacter: index });
    }

    closeFacility = () => {
        this.setState({ facility: null, selectedItem: null });
    }

    // The trainer's desk down in the dungeon opens the same hall as the town's
    openTraining = () => this.openFacility('training');
    closeTraining = () => this.closeFacility();

    // Pay the temple to put one character right: healing by the hit point,
    // and a levy on top for anyone who needs raising from the dead
    healAtTemple = (index) => {
        this.setState((prev) => {
            const character = prev.party[index];
            if (!character || prev.facility !== 'temple') return null;
            const blocker = templeBlocker(character, prev.gold);
            if (blocker) return { eventLog: [...prev.eventLog, { type: 'dev', message: blocker }] };

            const cost = healCost(character);
            const raised = character.condition === 'dead';
            playSounds(raised ? ['key', 'victory'] : ['heal'], 0.2);
            return {
                gold: prev.gold - cost,
                party: prev.party.map((c, i) => (i === index ? healed(c) : c)),
                eventLog: [...prev.eventLog, {
                    type: raised ? 'victory' : 'voice',
                    message: raised
                        ? `The priests call ${character.name} back from the dead for ${cost} gold.`
                        : `${character.name} is made whole again for ${cost} gold.`
                }]
            };
        });
    }

    // Take a room at the tavern: a night's rest, for the price of the bed
    takeRoom = () => {
        this.setState((prev) => {
            if (prev.facility !== 'tavern') return null;
            const blocker = roomBlocker(prev.gold);
            if (blocker) return { eventLog: [...prev.eventLog, { type: 'dev', message: blocker }] };

            playSound('rest');
            const rumour = RUMOURS[Math.floor(Math.random() * RUMOURS.length)];
            return {
                gold: prev.gold - ROOM_PRICE,
                party: restParty(prev.party),
                eventLog: [...prev.eventLog,
                    { type: 'voice', message: `The party takes rooms for the night, for ${ROOM_PRICE} gold. All are rested.` },
                    { type: 'dev', message: rumour }
                ]
            };
        });
    }

    // Buy something off the merchant's shelves
    buyItem = (itemId) => {
        this.setState((prev) => {
            if (prev.facility !== 'market' || !prev.shopStock) return null;
            const item = prev.shopStock.find((goods) => goods.id === itemId);
            const buyer = prev.party[prev.facilityCharacter];
            if (!item || !buyer) return null;
            const blocker = marketBlocker(item, prev.gold, buyer);
            if (blocker) return { eventLog: [...prev.eventLog, { type: 'dev', message: blocker }] };

            // It goes in the buyer's own pack, or nowhere
            const handed = giveItem(prev.party, item, prev.facilityCharacter);
            if (!handed.taker) {
                return { eventLog: [...prev.eventLog, { type: 'dev', message: `${buyer.name}'s pack is full.` }] };
            }
            const price = buyPrice(item, buyer);
            playSound('key');
            return {
                gold: prev.gold - price,
                party: handed.party,
                shopStock: prev.shopStock.filter((goods) => goods.id !== itemId),
                eventLog: [...prev.eventLog, {
                    type: 'voice',
                    message: `${handed.taker.name} buys the ${itemName(item)} for ${price} gold.`
                }]
            };
        });
    }

    // Sell something out of a character's pack. The merchant pays a share of
    // what it is worth, and puts it back on the shelves.
    sellItem = (index, itemId) => {
        this.setState((prev) => {
            if (prev.facility !== 'market') return null;
            const character = prev.party[index];
            const item = character && character.backpack.find((carried) => carried.id === itemId);
            if (!item) return null;

            const paid = sellPrice(item, character);
            playSound('chest');
            return {
                gold: prev.gold + paid,
                party: prev.party.map((c, i) => (i === index ? dropItem(c, itemId) : c)),
                shopStock: [...(prev.shopStock || []), item],
                selectedItem: null,
                eventLog: [...prev.eventLog, {
                    type: 'voice',
                    message: `${character.name} sells the ${itemName(item)} for ${paid} gold.`
                }]
            };
        });
    }

    // Pay to raise one character by a single level
    trainCharacter = (index) => {
        this.setState((prev) => {
            const character = prev.party[index];
            if (!character || prev.facility !== 'training') return null;
            const blocker = trainingBlocker(character, prev.gold);
            if (blocker) {
                return { eventLog: [...prev.eventLog, { type: 'dev', message: blocker }] };
            }
            const cost = trainingCost(character);
            const trained = levelUp(character);
            playSounds(['stairs', 'heal'], 0.2);
            const update = {
                gold: prev.gold - cost,
                party: prev.party.map((c, i) => (i === index ? trained : c)),
                eventLog: [...prev.eventLog, {
                    type: 'victory',
                    message: `${trained.name} trains to level ${trained.level} for ${cost} gold ` +
                             `(+${trained.hpGain} HP${trained.spGain ? `, +${trained.spGain} SP` : ''}).`
                }]
            };
            if (prev.run) {
                update.run = {
                    ...prev.run,
                    stats: { ...prev.run.stats, levels: (prev.run.stats.levels || 0) + 1 }
                };
            }
            return update;
        });
    }

    // Trainers also teach: a caster can buy a spell they do not yet know
    learnSpell = (index, spellId) => {
        this.setState((prev) => {
            const character = prev.party[index];
            const spell = ALL_SPELLS[spellId];
            // Spells are sold at the magic school, not by the trainer
            if (!character || !spell || prev.facility !== 'magic') return null;
            if (character.spells.includes(spellId)) return null;
            if (!spell.learnedBy || !spell.learnedBy.includes(character.className)) {
                return { eventLog: [...prev.eventLog, { type: 'dev', message: `${character.name} cannot learn ${spell.name}.` }] };
            }
            if (prev.gold < spell.price) {
                return { eventLog: [...prev.eventLog, { type: 'dev', message: `${spell.name} costs ${spell.price} gold.` }] };
            }
            playSounds(['key', 'spellCast'], 0.2);
            return {
                gold: prev.gold - spell.price,
                party: prev.party.map((c, i) => (i === index ? { ...c, spells: [...c.spells, spellId] } : c)),
                eventLog: [...prev.eventLog, {
                    type: 'victory',
                    message: `${character.name} learns ${spell.name} for ${spell.price} gold.`
                }]
            };
        });
    }

    // ---------- Spellcasting menus ----------

    // Open the casting menus. In combat the caster is whoever's turn it is.
    beginCast = () => {
        const prev = this.state;
        if (prev.busy || prev.gameOver || prev.screen !== 'playing') return;
        if (prev.combat) {
            const actor = prev.party.find((c) => c.id === currentActorId(prev.combat));
            if (!actor || !actor.spells.length) return;
            this.setState({ spellcasting: { stage: 'spell', casterId: actor.id } });
        } else {
            // Portrait clicks pick the caster now, so the sheet makes way
            this.setState({ spellcasting: { stage: 'caster' }, characterSheet: null });
        }
    }

    chooseCaster = (index) => {
        const { spellcasting, party } = this.state;
        const caster = party[index];
        if (!spellcasting || spellcasting.stage !== 'caster' || !caster) return;
        if (!isAble(caster)) {
            this.say(`${caster.name} is in no condition to cast.`, 'dev');
        } else if (!caster.spells.length) {
            this.say(`${caster.name} doesn't know any spells.`, 'dev');
        } else {
            this.setState({ spellcasting: { stage: 'spell', casterId: caster.id } });
        }
    }

    chooseSpell = (spellId) => {
        const { spellcasting, party, combat } = this.state;
        if (!spellcasting || spellcasting.stage !== 'spell') return;
        const caster = party.find((c) => c.id === spellcasting.casterId);
        const spell = SPELLS[spellId];
        if (!combat && !castableOutsideCombat(spell)) {
            this.say(`${spell.name} can only be cast in combat.`, 'dev');
        } else if (caster.sp < spellCost(spellId, caster)) {
            this.say(`${caster.name} doesn't have enough spell points for ${spell.name}.`, 'dev');
        } else if (spell.target === 'ally') {
            this.setState({ spellcasting: { ...spellcasting, stage: 'target', spellId } });
        } else {
            this.castChosenSpell(caster.id, spellId);
        }
    }

    chooseSpellTarget = (index) => {
        const { spellcasting, party } = this.state;
        const ally = party[index];
        if (!spellcasting || spellcasting.stage !== 'target' || !ally) return;
        const blocker = healBlocker(ally, SPELLS[spellcasting.spellId]);
        if (blocker) {
            // Stay on target selection so another character can be picked
            this.say(blocker, 'dev');
            return;
        }
        this.castChosenSpell(spellcasting.casterId, spellcasting.spellId, ally.id);
    }

    // Step back one menu; in combat the caster can't be changed
    cancelCast = () => {
        const { spellcasting, combat } = this.state;
        if (!spellcasting) return;
        if (spellcasting.stage === 'target') {
            this.setState({ spellcasting: { stage: 'spell', casterId: spellcasting.casterId } });
        } else if (spellcasting.stage === 'spell' && !combat) {
            this.setState({ spellcasting: { stage: 'caster' } });
        } else {
            this.setState({ spellcasting: null });
        }
    }

    // Portrait clicks (or number keys) pick the caster or the spell's target
    selectPortrait = (index) => {
        const { spellcasting, busy } = this.state;
        if (!spellcasting || busy) return;
        if (spellcasting.stage === 'caster') {
            this.chooseCaster(index);
        } else if (spellcasting.stage === 'target') {
            this.chooseSpellTarget(index);
        }
    }

    // Resolve a combat action; each new round, roaming monsters close in and may join
    resolveCombatAction(prev, action, options) {
        let result = performPartyAction(prev, action, { targetId: prev.combat.targetId, ...options });
        const effects = result.effects;
        if (result.combat && result.combat.round > prev.combat.round) {
            const coords = prev.player_coords;
            const stepped = stepMonsters(result.monsters, prev.player_map, coords);
            const joiners = stepped.filter((m) => !result.combat.monsterIds.includes(m.id) &&
                                                  m.stunned === 0 && isWithinReach(m, coords));
            const joined = joinCombat({ ...result, monsters: moveOntoParty(stepped, joiners, coords) }, joiners.map((m) => m.id));
            result = { ...joined, messages: [...result.messages, ...joined.messages], outcome: result.outcome };
        }
        return { update: this.applyCombatResult(prev, result), effects };
    }

    // Apply the result of a combat engine call to the game state
    applyCombatResult(prev, result, extra = {}) {
        let update = {
            party: result.party,
            monsters: result.monsters,
            combat: result.combat,
            gold: result.gold,
            eventLog: [...prev.eventLog, ...result.messages],
            ...extra
        };
        update = this.withRunStats(prev, update);
        update = this.addDrops(prev, update, result.kills);
        playSounds(result.sounds || []);
        if (result.outcome === 'defeat') {
            update.gameOver = true;
            if (prev.run) {
                Object.assign(update, this.endRun({ ...prev, ...update }, 'defeat'));
            }
        } else if (result.outcome === 'fled' && prev.retreatCoords &&
                   !monsterAt(result.monsters, prev.retreatCoords[0], prev.retreatCoords[1])) {
            update.player_coords = prev.retreatCoords;
        }
        return update;
    }
    
    // A game turn passes outside combat: monsters close in, and any that
    // reach the party start a fight
    advanceMonsters(state, previousCoords) {
        const coords = state.player_coords;
        const stepped = stepMonsters(state.monsters, state.player_map, coords);
        const attackers = stepped.filter((m) => m.hp > 0 && m.stunned === 0 && isWithinReach(m, coords));
        if (!attackers.length) {
            return { monsters: stepped };
        }
        // The party turns to face the first attacker, as in MM3
        const first = attackers.find((m) => m.x !== coords[0] || m.y !== coords[1]) || attackers[0];
        const heading = first.x === coords[0] && first.y === coords[1] ? state.player_heading : headingToward(coords, [first.x, first.y]);
        const monsters = moveOntoParty(stepped, attackers, coords);
        const result = startCombat({ ...state, monsters }, attackers.map((m) => m.id));
        return this.applyCombatResult(state, result, {
            player_heading: heading,
            retreatCoords: previousCoords || null
        });
    }

    initializeDoorStates(doors) {
        return doorStatesFrom(doors);
    }

    

    render() {
        const {children} = this.props;

        return (

            <React.Fragment>
                <StateContext.Provider value={{
                    ...this.state,
                    update: (obj) => {
                        this.setState(obj)
                    },
                    startRun: this.startRun,
                    saveParty: this.saveParty,
                    listParties,
                    deleteParty,
                    useStairs: this.useStairs,
                    returnToMenu: this.returnToMenu,
                    openEditor: this.openEditor,
                    closeEditor: this.closeEditor,
                    newSeed: randomSeed,
                    // Open the chest the party is standing on: the key, or gold.
                    // The gold is seeded per chest, so a run replays the same.
                    openChest: this.openChest,
                    // Called after the party moves: monsters close in and may start a fight
                    advanceWorld: (previousCoords) => {
                        this.setState((prev) => {
                            // Map what the party can now see (even if a fight is under way)
                            const explored = revealAround(prev.explored, prev.player_map, prev.player_coords);
                            const seen = explored === prev.explored ? {} : { explored };
                            if (prev.combat || prev.gameOver) return explored === prev.explored ? null : seen;
                            return { ...seen, ...this.advanceMonsters(prev, previousCoords) };
                        });
                    },
                    // Let a game turn pass without moving
                    wait: () => {
                        this.setState((prev) => {
                            if (prev.combat || prev.gameOver || prev.busy || prev.screen !== 'playing') return null;
                            const waited = { ...prev, eventLog: [...prev.eventLog, { type: 'voice', message: 'The party waits...' }] };
                            return { eventLog: waited.eventLog, ...this.advanceMonsters(waited, prev.player_coords) };
                        });
                    },
                    // Fire missile weapons at the nearest monster straight ahead.
                    // Takes a game turn, so monsters advance afterwards.
                    shoot: () => {
                        const prev = this.state;
                        if (prev.combat || prev.gameOver || prev.busy || prev.screen !== 'playing') return;
                        const say = (message) => this.setState({ eventLog: [...prev.eventLog, { type: 'voice', message }] });
                        if (!prev.party.some((c) => isAble(c) && c.missile)) {
                            say('No one in the party has a missile weapon!');
                            return;
                        }
                        const sight = findTargetInSight(prev.monsters, prev.player_map, prev.player_coords, prev.player_heading);
                        if (!sight) {
                            say('There is nothing in sight to shoot at.');
                            return;
                        }
                        const volley = rangedVolley(prev, sight.monster.id);
                        // Count the volley's kills and loot before the monsters advance,
                        // so what happens next is measured against the new tally
                        let shot = this.withRunStats(prev, {
                            party: volley.party,
                            monsters: volley.monsters,
                            gold: volley.gold
                        });
                        shot = this.addDrops(prev, shot, volley.kills);
                        const afterVolley = {
                            ...prev,
                            ...shot,
                            eventLog: [...(shot.eventLog || prev.eventLog), ...volley.messages]
                        };
                        this.playEffects(volley.effects, {
                            party: afterVolley.party,
                            gold: afterVolley.gold,
                            run: afterVolley.run,
                            eventLog: afterVolley.eventLog,
                            ...this.advanceMonsters(afterVolley, prev.player_coords)
                        });
                    },
                    combatAction: (action, options = {}) => {
                        const prev = this.state;
                        if (!prev.combat || prev.busy) return;
                        const { update, effects } = this.resolveCombatAction(prev, action, options);
                        this.playEffects(effects, update);
                    },
                    beginCast: this.beginCast,
                    chooseCaster: this.chooseCaster,
                    chooseSpell: this.chooseSpell,
                    chooseSpellTarget: this.chooseSpellTarget,
                    cancelCast: this.cancelCast,
                    selectPortrait: this.selectPortrait,
                    openCharacterSheet: this.openCharacterSheet,
                    closeCharacterSheet: this.closeCharacterSheet,
                    useItem: this.useItem,
                    selectItem: this.selectItem,
                    transferItem: this.transferItem,
                    bashDoor: this.bashDoor,
                    toggleGameMenu: this.toggleGameMenu,
                    closeGameMenu: this.closeGameMenu,
                    toggleMap: this.toggleMap,
                    closeMap: this.closeMap,
                    openTraining: this.openTraining,
                    closeTraining: this.closeTraining,
                    openFacility: this.openFacility,
                    chooseFacilityCharacter: this.chooseFacilityCharacter,
                    closeFacility: this.closeFacility,
                    healAtTemple: this.healAtTemple,
                    takeRoom: this.takeRoom,
                    buyItem: this.buyItem,
                    sellItem: this.sellItem,
                    trainCharacter: this.trainCharacter,
                    learnSpell: this.learnSpell,
                    teachableTo,
                    selectTarget: (monsterId) => {
                        this.setState((prev) => prev.combat ? { combat: { ...prev.combat, targetId: monsterId } } : null);
                    },
                    rest: () => {
                        this.setState((prev) => {
                            if (prev.combat || prev.gameOver || prev.busy || prev.screen !== 'playing') return null;
                            if (prev.town) {
                                playSound('bump');
                                return { eventLog: [...prev.eventLog, { type: 'voice',
                                    message: 'You cannot bed down in the street. The tavern rents rooms.' }] };
                            }
                            const [px, py] = prev.player_coords;
                            const nearby = prev.monsters.some((m) => Math.abs(m.x - px) + Math.abs(m.y - py) <= 5);
                            if (nearby) {
                                return { eventLog: [...prev.eventLog, { type: 'voice', message: "You can't rest with enemies nearby!" }] };
                            }
                            playSound('rest');
                            return {
                                party: restParty(prev.party),
                                eventLog: [...prev.eventLog, { type: 'voice', message: 'The party rests and recovers its strength.' }]
                            };
                        });
                    },
                    // Inventory: wear, remove or throw away an item
                    equipItem: (index, itemId) => {
                        this.setState((prev) => {
                            const character = prev.party[index];
                            if (!character) return null;
                            playSound('click');
                            return {
                                party: prev.party.map((c, i) => (i === index ? equipItem(c, itemId) : c)),
                                selectedItem: null
                            };
                        });
                    },
                    unequipSlot: (index, slot) => {
                        this.setState((prev) => {
                            const character = prev.party[index];
                            if (!character) return null;
                            const updated = unequipSlot(character, slot);
                            if (updated === character) {
                                return { eventLog: [...prev.eventLog, { type: 'dev', message: `${character.name}'s pack is full.` }] };
                            }
                            playSound('click');
                            return { party: prev.party.map((c, i) => (i === index ? updated : c)) };
                        });
                    },
                    dropItem: (index, itemId) => {
                        this.setState((prev) => {
                            const character = prev.party[index];
                            if (!character) return null;
                            const item = character.backpack.find((i) => i.id === itemId);
                            if (!item) return null;
                            playSound('bump');
                            return {
                                selectedItem: null,
                                party: prev.party.map((c, i) => (i === index ? dropItem(c, itemId) : c)),
                                eventLog: [...prev.eventLog, { type: 'voice', message: `${character.name} drops the ${itemName(item)}.` }]
                            };
                        });
                    },
                    // Run the same dungeon again, or a fresh one
                    replayRun: () => {
                        const result = this.state.runResult;
                        if (result) this.startRun(result.seed, result.levels);
                    },
                    restartGame: () => {
                        const result = this.state.runResult;
                        this.startRun(randomSeed(), result ? result.levels : DEFAULT_RUN_LEVELS);
                    },
                    toggleDoor: this.toggleDoor,
                    interact: this.interact,
                    describeHere: this.describeHere,
                    checkAdjacentDoor: (x, y) => {
                        const directions = [[0, 1], [1, 0], [0, -1], [-1, 0]];
                        for (const [dx, dy] of directions) {
                            const checkX = x + dx;
                            const checkY = y + dy;
                            if (checkY >= 0 && checkY < this.state.player_map.length &&
                                checkX >= 0 && checkX < this.state.player_map[0].length) {
                                const tile = this.state.player_map[checkY][checkX];
                                // Check if it's any door type (3-6)
                                if (tile >= 3 && tile <= 6) {
                                    const isOpen = tile === 4 || tile === 6;
                                    return {x: checkX, y: checkY, isOpen: isOpen};
                                }
                            }
                        }
                        return null;
                    }
                }}>
                    {children}
                </StateContext.Provider>
            </React.Fragment>
        );
    }
}

export function withContext(ComposedComponent) {
    class withContext extends Component {

        render() {

            return (
                <StateContext.Consumer>
                    {
                        (context) => (
                            <ComposedComponent context={context} {...this.props}/>
                        )
                    }
                </StateContext.Consumer>
            );
        }
    }

    return withContext;
}

